/** Canonical Avery proposal contract shared by the Worker and ops console. */

import type { AveryActionKind } from './avery-actions'

/**
 * Kinds with bespoke propose and execute code (each has its own branch in the
 * console's executeProposal.ts). New kinds do not go here: a route-backed kind
 * is declared once in ./avery-actions.ts (AVERY_ROUTE_ACTIONS) and the full
 * kind set is this list plus those.
 */
export const AVERY_PROPOSAL_KINDS = [
  'refund', 'resolve_ticket',
  'update_rates_base', 'update_rates_event', 'add_zone_photo', 'add_event',
  'add_time_of_day_rate', 'remove_time_of_day_rate',
  'cancel_subscription', 'change_subscription_plan',
  'set_base_rate', 'set_graduated_schedule', 'set_min_duration',
  'extend_session', 'update_zone_fees',
  'reopen_ticket', 'escalate_ticket', 'resync_plate',
  'update_zone_config', 'create_zone', 'upsert_validation', 'delete_validation',
  'set_daily_max', 'set_monthly_plan', 'set_day_of_week_rate',
  'set_pass', 'set_demand_tiers',
  'create_client', 'invite_team_member', 'send_payout_link',
  'create_facility', 'update_fee_model', 'set_contract_terms',
  'set_duration_ladder', 'set_rate_product',
] as const

export type AveryLegacyProposalKind = typeof AVERY_PROPOSAL_KINDS[number]

export type AveryProposalKind = AveryLegacyProposalKind | AveryActionKind

/** Proposal carried between the Avery Worker, Ops console, and demo. Keep this
 * wire shape stable while the proposal store is independently feature-flagged. */
export interface AveryProposal {
  kind: AveryProposalKind
  /** Per-kind inputs; semantic payload validation remains at the apply boundary. */
  payload: Record<string, unknown>
  reason?: string
  /** Present only when the proposal store is enabled. */
  id?: string
  correlationId?: string
}

/** Persisted proposal fields serialized by GET /api/avery/proposals and
 *  GET /api/avery/proposals/:id. The Worker owns its storage record type; this
 *  contract describes only the API response consumed by Ops. */
export interface AveryProposalRecordResponse {
  id: string
  correlationId: string
  clientId: string
  zoneId: string | null
  kind: AveryProposalKind
  payload: Record<string, unknown>
  reason: string | null
  proposedBy: string
  status: 'pending' | 'executed' | 'expired' | 'superseded'
  createdAt: string
  executedAt: string | null
  expiresAt: string | null
}

/** Safe, non-money kinds supported by the authoritative Worker apply path. */
export const AVERY_SERVER_APPLY_KINDS = [
  'update_rates_base', 'set_base_rate', 'set_min_duration',
  'add_time_of_day_rate', 'set_day_of_week_rate', 'set_pass',
  'set_demand_tiers', 'update_zone_fees', 'remove_time_of_day_rate',
  'set_duration_ladder', 'set_rate_product',
] as const satisfies readonly AveryProposalKind[]

export const AVERY_SERVER_APPLY_KIND_SET: ReadonlySet<AveryProposalKind> =
  new Set(AVERY_SERVER_APPLY_KINDS)

/** Actions that move money or charge a saved card. */
export const AVERY_MONEY_DENYLIST_KINDS = [
  'refund', 'extend_session',
] as const satisfies readonly AveryProposalKind[]

export const AVERY_MONEY_DENYLIST_KIND_SET: ReadonlySet<string> =
  new Set(AVERY_MONEY_DENYLIST_KINDS)
